import { useEffect, useState } from 'react';

interface IBreakpoints {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  '2xl': number;
}

const breakpoints: IBreakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1440,
};

const useMedia = (breakpoints: IBreakpoints) => {
  const [currentBreakpoints, setCurrentBreakpoints] =
    useState<IBreakpoints>(breakpoints);

  useEffect(() => {
    const handleResize = () => {
      const updatedBreakpoints: IBreakpoints = { ...breakpoints };
      setCurrentBreakpoints(updatedBreakpoints);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [breakpoints]);

  const media = (mediaQueryString: string): boolean => {
    return window.matchMedia(mediaQueryString).matches;
  };

  const query = (mediaQuery: string): boolean => {
    return media(mediaQuery);
  };

  const isMobile = () => {
    return down(currentBreakpoints.md);
  };

  const min = (breakpoint: number) => {
    return query(`(min-width: ${breakpoint}px)`);
  };

  const max = (breakpoint: number) => {
    return query(`(max-width: ${breakpoint}px)`);
  };

  const up = (breakpoint: number): boolean => {
    return min(breakpoint);
  };

  const down = (breakpoint: number): boolean => {
    return max(breakpoint);
  };

  const between = (from: number, to: number): boolean => {
    return query(`(min-width: ${from}px) and (max-width: ${to}px)`);
  };

  const isTouch = () => {
    return query(`(pointer: coarse)`);
  };

  const isMouse = () => {
    return query(`(pointer: fine)`);
  };

  const xs = () => {
    return up(currentBreakpoints.xs);
  };

  const sm = () => {
    return up(currentBreakpoints.sm);
  };

  const md = () => {
    return up(currentBreakpoints.md);
  };

  const lg = () => {
    return up(currentBreakpoints.lg);
  };

  const xl = () => {
    return up(currentBreakpoints.xl);
  };

  return {
    isMobile,
    min,
    max,
    up,
    down,
    between,
    isTouch,
    isMouse,
    xs,
    sm,
    md,
    lg,
    xl,
  };
};

export const Media = () => {
  const media = useMedia(breakpoints);

  return media;
};

export default useMedia;
